import SeqPager from 'admin/components/organisms/SeqPager';
import TransactionBadge from 'admin/components/shared/TransactionBadge';
import { formatDate, formatUUID } from 'admin/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { fetchTransactionsV2 } from '../../../actions/transactions';
import { useAppSelector } from '../../../hooks/useApp';
import Table from '../../atoms/Table';

type Props = {
  transactions?: Awaited<ReturnType<typeof fetchTransactionsV2>>;
  privateMoneyId: string;
  onChangePage: (paginatedLink: string | null) => void;
};

type Header = {
  label: string;
  size: number;
  align: 'center' | 'justify' | 'left' | 'right';
  tooltipKey?: string;
  tooltipText?: string;
};

const FormWrapper = styled.div``;
const TransactionBadgeWrapper = styled(TransactionBadge)`
  font-size: 12px;
  font-weight: bold;
  border-radius: 5px;
`;

const TooltipTableCell = styled(Table.Cell)<{ tooltip?: boolean }>`
  &:hover {
    ${({ tooltip }) => tooltip && 'cursor: pointer; background-color: #eeeeee;'}
  }
`;

const createHeaders = (canReadPersonalInfo: boolean): Header[] =>
  canReadPersonalInfo
    ? [
        { label: '取引種別', size: 1, align: 'left' },
        {
          label: '取引ID',
          size: 1,
          align: 'left',
          tooltipKey: 'transactionId',
          tooltipText: '取引ごとに自動採番されるID*先頭8桁を表示',
        },
        { label: '日時', size: 2, align: 'left' },
        { label: '店舗名 （組織名）', size: 3.5, align: 'left' },
        {
          label: '端末ID',
          size: 1,
          align: 'left',
          tooltipKey: 'deviceId',
          tooltipText: '支払い/チャージで使用したPokepay専用端末のID*先頭8桁を表示',
        },
        {
          label: 'お客様ニックネーム',
          size: 1.5,
          align: 'left',
        },
        {
          label: 'お客様ID',
          size: 1,
          align: 'left',
          tooltipKey: 'customerId',
          tooltipText: 'お客様ごとに自動採番されるID*先頭8桁を表示',
        },
        { label: '総額', size: 1, align: 'right' },
      ]
    : [
        { label: '取引種別', size: 1, align: 'left' },
        {
          label: '取引ID',
          size: 1,
          align: 'left',
          tooltipKey: 'transactionId',
          tooltipText: '取引ごとに自動採番されるID*先頭8桁を表示',
        },
        { label: '日時', size: 2, align: 'left' },
        { label: '店舗名 （組織名）', size: 4, align: 'left' },
        {
          label: '端末ID',
          size: 1,
          align: 'left',
          tooltipKey: 'deviceId',
          tooltipText: '支払い/チャージで使用したPokepay専用端末のID*先頭8桁を表示',
        },
        {
          label: 'お客様ID',
          size: 1,
          align: 'left',
          tooltipKey: 'customerId',
          tooltipText: 'お客様ごとに自動採番されるID*先頭8桁を表示',
        },
        { label: '総額', size: 1, align: 'right' },
      ];

const ListTable = ({ transactions, privateMoneyId, onChangePage }: Props) => {
  const user = useAppSelector((state) => state.user);
  const canReadPersonalInfo = user.isAllowedTo('read', 'personalInfo');

  const headers = createHeaders(canReadPersonalInfo);

  return (
    <FormWrapper>
      <Table styles={{ margin: '5px' }}>
        <Table.Header>
          <Table.Row>
            {headers.map((c) => (
              <TooltipTableCell
                size={c.size}
                key={c.label}
                align={c.align}
                tooltip={!!c.tooltipKey && !!c.tooltipText}
                data-tip
                data-for={c.tooltipKey}
              >
                {c.label}
                {!!c.tooltipKey && !!c.tooltipText && (
                  <ReactTooltip id={c.tooltipKey} type="dark" place="top" effect="solid">
                    <span>{c.tooltipText}</span>
                  </ReactTooltip>
                )}
              </TooltipTableCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {transactions?.rows?.map((transaction) => (
            <Table.Row key={transaction.id} styles={{ height: '60px' }}>
              <Table.Cell>
                <TransactionBadgeWrapper transaction={transaction} />
              </Table.Cell>
              <Table.Cell wordBreak="keep-all">
                <Link to={`/transactions/${transaction.id}?private_money_id=${privateMoneyId}`}>
                  {formatUUID(transaction.id)}
                </Link>
              </Table.Cell>
              <Table.Cell>{formatDate(transaction.done_at)}</Table.Cell>
              <Table.Cell wordBreak="break-word">
                {transaction.shop_name || '無名の店舗'}{' '}
                {transaction.organization_name ? `（${transaction.organization_name}）` : null}
              </Table.Cell>
              <Table.Cell wordBreak="keep-all">{formatUUID(transaction.terminal_id)}</Table.Cell>
              {canReadPersonalInfo ? <Table.Cell>{transaction.customer_name}</Table.Cell> : null}
              <Table.Cell wordBreak="keep-all">
                {canReadPersonalInfo ? (
                  <Link to={`/customers/${transaction.user_id}`}>
                    {formatUUID(transaction.user_id)}
                  </Link>
                ) : (
                  formatUUID(transaction.user_id)
                )}
              </Table.Cell>
              <Table.Cell align="right">
                ¥{(transaction.money_amount + transaction.raw_point_amount).toLocaleString()}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row noBorder>
            <Table.Cell colSpan={headers.length} align="center" styles={{ paddingTop: '16px' }}>
              {transactions && (
                <SeqPager
                  prev={transactions.prev}
                  next={transactions.next}
                  onChangePage={onChangePage}
                />
              )}
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </FormWrapper>
  );
};

export default ListTable;
